<script lang="ts" setup>
import { useEventStore } from '@/stores';
import { ref } from 'vue';
import { AlertMessage, ContentComp, SavedChangesMessage, TabComp, UnsavedChangesMessage } from '@/components';
import { useTaskLoader } from '@/composeables/loaders';
import type DetailViewExpose from '@/objects/DetailViewExpose';
import { computed } from 'vue';
import { FieldAlertMessage } from '@/objects';
import { TabContainer, TabDefinition } from '@/objects/Tabs';
import StationGeneralView from './StationGeneralView.vue';
import StationPositionView from './StationPositionView.vue';
import { IntArray, StationDetails } from '@/objects/generated';
import { StationDetailsDao } from '@/dao';

/**
 * The props for this view
 */
const props = defineProps<{
    /**
     * The ID of the station we are showing
     */
    id: number;
}>();

/**
 * Callback telling us if the component is visible or not
 * @param visible
 */
const isVisible = (visible: boolean) => {
    // Here we can handle keyboard shortcuts etc
};

const eventStore = useEventStore();

// The internal station object that we are editing
const station = ref<StationDetails | null>(null);

// The detail editors for the general view
const generalViewDetailEditors = ref<{ messages: FieldAlertMessage[]; canSave: boolean } | null>(null);

// We show the success message when the station is saved
const allChangesSaved = ref<boolean>(false);

// We show the error message when the station is saved
const saveErrorMessage = ref<string | null>(null);

const { AddLoader, LoadState } = useTaskLoader();

const tabs = new TabContainer();
tabs.AddTab(new TabDefinition('general', 'station.general', 'fa-edit'));
tabs.AddTab(new TabDefinition('position', 'station.position', 'fa-map-location-dot'));

AddLoader(async () => {
    const ids = IntArray.FromJsValues({
        Values: [props.id],
    });

    const stationDetails = (await StationDetailsDao.GetStationDetails(eventStore.eventID, ids)) ?? StationDetails.FromJsValues({});

    if (stationDetails == null || stationDetails.length == 0) {
        // We need to find the id of the StationType Normal
        station.value = StationDetails.FromJsValues({ EventID: eventStore.eventID, StationTypeID: 0 });
    } else {
        station.value = stationDetails[0];
    }
}, 'station.loading_station');

const changed = computed(() => (station.value?.EntityState ?? 'Unchanged') !== 'Unchanged');

defineExpose<DetailViewExpose<StationDetails>>({
    val: computed(() => station.value),
    title: computed(() => station.value?.Name ?? ''),
    changed: changed,
    messages: computed(() => {
        let messages: FieldAlertMessage[] = [];

        generalViewDetailEditors.value?.messages.forEach((message) => {
            messages.push(message);
        });

        return messages;
    }),
    disableSave: computed(() => generalViewDetailEditors.value?.canSave === false),
});
</script>

<template>
    <ContentComp
        :load-state="LoadState"
        @isVisible="isVisible"
    >
        <AlertMessage
            type="danger"
            :show-icon="false"
            v-if="saveErrorMessage != null"
            :message="saveErrorMessage"
            ><span v-html="$tm(saveErrorMessage, '', true)"></span
        ></AlertMessage>

        <UnsavedChangesMessage v-if="changed && (generalViewDetailEditors == null || generalViewDetailEditors!.messages.length == 0)" />
        <SavedChangesMessage :visible="allChangesSaved" />
        <TabComp
            :tabs="tabs"
            :pinable="false"
            v-if="station != null"
        >
            <template #tab-general>
                <StationGeneralView
                    v-model="station"
                    ref="generalViewDetailEditors"
                />
            </template>
            <template #tab-position>
                <StationPositionView v-model="station" />
            </template>
        </TabComp>
    </ContentComp>
</template>
